// Runtime checks of app-v1 (both directions, WebSocket and HTTP) with the precompiled validators generated from
// docs/protocol/app-v1. No ajv compiler at runtime: the app runs under a CSP without eval.
import * as validators from './generated/validators.js';
import { MESSAGE_SCHEMAS, type MessageSchemaName } from './generated/catalogs.js';
import type { ClientMessage, SchemaTypes, ServerMessage } from './generated/types.js';

export type Parsed<T> = { ok: true; value: T } | { ok: false; error: string; issues?: string[] };

interface CompiledValidator {
  (data: unknown): boolean;
  errors?: { instancePath: string; message?: string; keyword: string }[] | null;
}

const camel = (name: string): string => name.replace(/-([a-z0-9])/g, (_m, c: string) => c.toUpperCase());

const byName = new Map<MessageSchemaName, CompiledValidator>(
  MESSAGE_SCHEMAS.map((name) => [
    name,
    (validators as unknown as Record<string, CompiledValidator>)[camel(name)] as CompiledValidator,
  ]),
);

const isObj = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

function issuesOf(fn: CompiledValidator): string[] {
  return (fn.errors ?? []).map((e) => `${e.instancePath || '/'} ${e.message ?? e.keyword}`);
}

/** Validates data against one message schema of app-v1 (e.g. 'http-auth-session'). */
export function validate<K extends MessageSchemaName>(name: K, data: unknown): Parsed<SchemaTypes[K]> {
  const fn = byName.get(name);
  if (!fn) throw new Error(`unknown app-v1 schema ${name}`);
  return fn(data)
    ? { ok: true, value: data as SchemaTypes[K] }
    : { ok: false, error: 'malformed', issues: issuesOf(fn) };
}

const CLIENT_TYPES = new Set(['hello', 'sub', 'unsub', 'cmd']);

/** Checks a message from a browser. Unknown message types: unknown_message; unknown fields are ignored. */
export function parseClientMessage(raw: unknown): Parsed<ClientMessage> {
  if (!isObj(raw) || typeof raw.t !== 'string') return { ok: false, error: 'malformed' };
  if (!CLIENT_TYPES.has(raw.t)) return { ok: false, error: 'unknown_message' };
  return validate('ws-client', raw);
}

/** Checks a message from the API (the app core trusts nothing it cannot read). */
export function parseServerMessage(raw: unknown): Parsed<ServerMessage> {
  return validate('ws-server', raw);
}

export const CMD_ID_PATTERN = /^[A-Za-z0-9_-]{8,36}$/;
export const MACHINE_ID_PATTERN = /^m[0-9a-hjkmnp-tv-z]{20}$/;
